import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTeam } from '../hooks/useTeams';
import useTitle from '../hooks/useTitle';
import ConfirmActionModal from '../components/Modals/ConfirmActionModal';
import '../components/Dashboard/TeamsSection.css';

const TeamPage = () => {
    const { teamId } = useParams();
    const { team, members, loading, updateTeam, inviteMember, removeMember, changeRole } = useTeam(teamId);

    // Set dynamic title based on team name
    useTitle(team ? team.name : 'Team Details');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({ name: '', description: '' });
    const [editErrors, setEditErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Confirmation modal state
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
    const [confirmRemoveLoading, setConfirmRemoveLoading] = useState(false);
    const [confirmRemoveError, setConfirmRemoveError] = useState(null);
    const [confirmRemoveSuccess, setConfirmRemoveSuccess] = useState(false);
    const [memberPendingRemoval, setMemberPendingRemoval] = useState(null);

    const handleInvite = async (e) => {
        e.preventDefault();
        try {
            await inviteMember(inviteEmail, inviteRole);
            setInviteEmail('');
            setInviteRole('member');
            setShowInviteModal(false);
        } catch (err) {
            // Error handled by hook
        }
    };

    const openEditModal = () => {
        setEditFormData({
            name: team.name || '',
            description: team.description || ''
        });
        setEditErrors({});
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditErrors({});
        setIsSubmitting(true);

        try {
            await updateTeam(editFormData);
            setShowEditModal(false);
        } catch (err) {
            if (err.status === 422) {
                setEditErrors(err.errors);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const openRemoveMemberConfirm = (member) => {
        setMemberPendingRemoval(member);
        setConfirmRemoveLoading(false);
        setConfirmRemoveError(null);
        setConfirmRemoveSuccess(false);
        setConfirmRemoveOpen(true);
    };

    const closeRemoveModal = () => {
        setConfirmRemoveOpen(false);
        setMemberPendingRemoval(null);
        setConfirmRemoveError(null);
        setConfirmRemoveSuccess(false);
    };

    const handleConfirmRemoveMember = async () => {
        if (!memberPendingRemoval) return;

        setConfirmRemoveLoading(true);
        setConfirmRemoveError(null);

        try {
            await removeMember(memberPendingRemoval.user_id);
            setConfirmRemoveSuccess(true);
        } catch (err) {
            setConfirmRemoveError(err.message || 'Failed to remove member');
        } finally {
            setConfirmRemoveLoading(false);
        }
    };

    if (loading) {
        return <div className="loading">Loading team...</div>;
    }

    if (!team) {
        return <div className="error-state">Team not found</div>;
    }

    const isOwner = team.owner_id === JSON.parse(localStorage.getItem('user') || '{}').id;

    return (
        <div className="team-page">
            <div className="team-header">
                <div>
                    <h1>{team.name}</h1>
                    <p>{team.description}</p>
                </div>
                {isOwner && (
                    <div className="header-actions">
                        <button className="btn-secondary" onClick={openEditModal}>
                            Edit Team
                        </button>
                        <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
                            Invite Member
                        </button>
                    </div>
                )}
            </div>

            <div className="team-section">
                <h2>Members ({members.length})</h2>
                <div className="members-list">
                    {members.map((member) => (
                        <div key={member.id} className="member-row">
                            <div className="member-info">
                                <strong>{member.user?.name || 'Unknown'}</strong>
                                <span className="member-email">{member.user?.email}</span>
                            </div>
                            <div className="member-actions">
                                <span className={`role-badge ${member.role}`}>{member.role}</span>
                                {isOwner && member.role !== 'owner' && (
                                    <>
                                        <select
                                            value={member.role}
                                            onChange={(e) => changeRole(member.user_id, e.target.value)}
                                            className="role-select"
                                        >
                                            <option value="member">Member</option>
                                            <option value="owner">Owner</option>
                                        </select>
                                        <button
                                            className="btn-danger-sm"
                                            onClick={() => openRemoveMemberConfirm(member)}
                                        >
                                            Remove
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showInviteModal && (
                <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Invite Member</h2>
                        <form onSubmit={handleInvite}>
                            <div className="form-group">
                                <label>Email Address *</label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Role</label>
                                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                                    <option value="member">Member</option>
                                    <option value="owner">Owner</option>
                                </select>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowInviteModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary">
                                    Send Invite
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showEditModal && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Edit Team</h2>
                        <form onSubmit={handleEditSubmit}>
                            <div className="form-group">
                                <label>Team Name *</label>
                                <input
                                    type="text"
                                    value={editFormData.name}
                                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                    required
                                    placeholder="Enter team name"
                                />
                                {editErrors.name && <span className="error">{editErrors.name[0]}</span>}
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={editFormData.description}
                                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                    rows="3"
                                    placeholder="Enter team description (optional)"
                                />
                                {editErrors.description && <span className="error">{editErrors.description[0]}</span>}
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmActionModal
                isOpen={confirmRemoveOpen}
                title={confirmRemoveSuccess ? "Member Removed" : "Remove team member"}
                description={
                    memberPendingRemoval
                        ? `Are you sure you want to remove ${memberPendingRemoval.user?.name || 'this member'} from "${team?.name}"? They will lose access to this team's resources.`
                        : 'Are you sure you want to remove this member?'
                }
                confirmLabel="Remove member"
                cancelLabel="Cancel"
                successLabel="Done"
                loading={confirmRemoveLoading}
                success={confirmRemoveSuccess}
                error={confirmRemoveError}
                onConfirm={handleConfirmRemoveMember}
                onCancel={closeRemoveModal}
                confirmDisabled={!memberPendingRemoval}
                renderAfterSuccess={
                    <div className="confirm-success-content">
                        <div className="success-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" fill="#10B981" />
                                <path d="M8 12l2.5 2.5L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <p className="success-message">
                            <strong>{memberPendingRemoval?.user?.name}</strong> has been removed from the team.
                        </p>
                        <p className="updated-count">
                            Team now has <strong>{members.length}</strong> member{members.length !== 1 ? 's' : ''}.
                        </p>
                    </div>
                }
            />
        </div>
    );
};

export default TeamPage;
